import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    ArrowLeft, Star, Share2, Calendar, ShieldCheck,
    MessageSquare, Briefcase, MapPin, Globe, Loader2,
    CheckCircle2, AlertCircle, Send, Plus
} from 'lucide-react';
import { ServicesService } from '../services/servicesService';
import { ProvidersService } from '../services/providerService';
import { useAuth } from '../context/AuthContext';
import { BookingModal } from '../components/interactions/BookingModal';
import { updateMetaTags } from '../utils/metaUtils';
import { BookingsService } from '../services/bookingService';

const ServicePage = () => {
    const { serviceId } = useParams();
    const navigate = useNavigate();
    const { user, userProfile } = useAuth();

    const [service, setService] = useState(null);
    const [provider, setProvider] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            try {
                const serviceData = await ServicesService.getById(serviceId);
                if (serviceData) {
                    setService(serviceData);
                    updateMetaTags({
                        title: serviceData.title,
                        description: serviceData.description,
                        image: serviceData.imageUrl,
                        url: window.location.href
                    });

                    const providerData = await ProvidersService.getById(serviceData.providerId);
                    setProvider(providerData);
                } else {
                    setError('Service not found');
                }
            } catch (err) {
                console.error('Error loading service:', err);
                setError('Failed to load service details');
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, [serviceId]);

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: service.title,
                    text: service.description,
                    url: window.location.href,
                });
            } catch (err) {
                console.log('Error sharing:', err);
            }
        } else {
            // Fallback: Copy to clipboard
            navigator.clipboard.writeText(window.location.href);
            alert('Link copied to clipboard!');
        }
    };

    const handleBookingSubmit = async (data) => {
        try {
            await BookingsService.create({
                serviceId: service.id,
                serviceTitle: service.title,
                providerId: service.providerId,
                providerName: provider?.name || 'Unknown',
                clientId: user?.uid || 'guest',
                clientName: user?.displayName || userProfile?.displayName || data.name,
                clientEmail: user?.email || data.email,
                mobile: data.mobile,
                urgency: data.urgency,
                date: data.date,
                scope: data.scope,
                priceStart: service.priceStart,
            });
            alert(`Success! Your booking request for "${service?.title}" has been sent.`);
            setIsBookingModalOpen(false);
        } catch (error) {
            console.error('Booking error:', error);
            alert('Failed to submit booking. Please try again.');
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
                    <p className="text-slate-600 font-medium">Loading service details...</p>
                </div>
            </div>
        );
    }

    if (error || !service) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <AlertCircle className="w-8 h-8 text-red-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">{error || 'Something went wrong'}</h2>
                    <p className="text-slate-500 mb-8">We couldn't find the service you're looking for. It might have been removed or unpublished.</p>
                    <button
                        onClick={() => navigate('/')}
                        className="w-full bg-indigo-600 text-white py-3 rounded-xl font-bold hover:bg-indigo-700 transition-colors shadow-lg"
                    >
                        Back to Home
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 pb-20">
            {/* Navigation Backdrop */}
            <div className="bg-white border-b border-slate-100 sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-slate-600 hover:text-indigo-600 font-medium transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        <span>Back</span>
                    </button>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleShare}
                            className="p-2.5 rounded-full bg-slate-50 text-slate-600 hover:bg-indigo-50 hover:text-indigo-600 transition-all shadow-sm border border-slate-100"
                            title="Share Service"
                        >
                            <Share2 className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Image Gallery / Main Image */}
                        <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-white border border-slate-100 aspect-video lg:aspect-[16/9]">
                            {service.imageUrl ? (
                                <img src={service.imageUrl} alt={service.title} className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full bg-gradient-to-br from-indigo-100 via-purple-50 to-white flex items-center justify-center">
                                    <span className="text-8xl">🎨</span>
                                </div>
                            )}
                            <div className="absolute top-6 left-6 flex gap-2">
                                <span className="bg-white/95 backdrop-blur-sm px-4 py-1.5 rounded-full text-xs font-bold text-indigo-600 shadow-lg uppercase tracking-wider">
                                    {service.category || 'Service'}
                                </span>
                                {service.status === 'published' && (
                                    <span className="bg-green-500/90 backdrop-blur-sm px-4 py-1.5 rounded-full text-xs font-bold text-white shadow-lg uppercase tracking-wider flex items-center gap-1.5">
                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                        Verified
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Title and Stats */}
                        <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100">
                            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
                                <div>
                                    <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 leading-tight mb-4">
                                        {service.title}
                                    </h1>
                                    <div className="flex flex-wrap items-center gap-4 text-sm font-medium text-slate-500">
                                        <div className="flex items-center gap-1.5">
                                            <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                                            <span className="text-slate-900 font-bold">{service.rating || 0}</span>
                                            <span className="text-slate-400">({service.reviews || 0} reviews)</span>
                                        </div>
                                        <div className="w-1.5 h-1.5 rounded-full bg-slate-300 hidden md:block"></div>
                                        <div className="flex items-center gap-1.5">
                                            <Calendar className="w-5 h-5 text-slate-400" />
                                            <span>Updated {new Date(service.updatedAt?.seconds * 1000).toLocaleDateString()}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Tags */}
                            {service.tags && service.tags.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-8">
                                    {service.tags.map(tag => (
                                        <span key={tag} className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-lg text-xs font-bold ring-1 ring-indigo-100">
                                            #{tag}
                                        </span>
                                    ))}
                                </div>
                            )}

                            <div className="prose prose-slate max-w-none">
                                <h3 className="text-xl font-bold text-slate-900 mb-4">Description</h3>
                                <p className="text-slate-600 leading-relaxed whitespace-pre-line text-lg">
                                    {service.description}
                                </p>
                            </div>
                        </div>

                        {/* Included in Service */}
                        <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100">
                            <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                                <ShieldCheck className="w-6 h-6 text-green-500" />
                                Why choose this service?
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {[
                                    'Professional quality guaranteed',
                                    'Clear communication throughout',
                                    'Secure ordering process',
                                    'Timed delivery',
                                    'Unlimited revisions (optional)',
                                    'Support available'
                                ].map((item, idx) => (
                                    <div key={idx} className="flex items-center gap-3 p-3 rounded-2xl hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-100">
                                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                                        <span className="text-slate-700 font-medium">{item}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Pricing Card */}
                        <div className="bg-white rounded-3xl p-8 shadow-xl border border-slate-100 sticky top-24">
                            <div className="mb-6">
                                <span className="text-slate-500 text-sm font-medium">Starting from</span>
                                <div className="flex items-baseline gap-1">
                                    <span className="text-4xl font-black text-indigo-600 tracking-tight">R{service.priceStart}</span>
                                    <span className="text-slate-400 font-medium whitespace-nowrap">/ project</span>
                                </div>
                            </div>

                            <div className="space-y-4 mb-8">
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-500">Service Fee</span>
                                    <span className="text-slate-900 font-bold">Included</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-500">Response Time</span>
                                    <span className="text-slate-900 font-bold">~ 24 hours</span>
                                </div>
                                <div className="p-4 bg-indigo-50 rounded-2xl flex items-center gap-3">
                                    <ShieldCheck className="w-5 h-5 text-indigo-600" />
                                    <span className="text-xs text-indigo-700 font-medium leading-tight">
                                        Protected by Parrot Secure Payments. Funds are held until delivery is confirmed.
                                    </span>
                                </div>
                            </div>

                            <button
                                onClick={() => setIsBookingModalOpen(true)}
                                className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-black text-lg hover:bg-indigo-700 transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-indigo-100 flex items-center justify-center gap-2 mb-4"
                            >
                                Request Booking <Send className="w-5 h-5" />
                            </button>

                            <button
                                className="w-full bg-white text-slate-700 py-4 rounded-2xl border-2 border-slate-100 font-bold hover:bg-slate-50 transition-all flex items-center justify-center gap-2"
                            >
                                Contact Provider <MessageSquare className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Provider Section */}
                        {provider && (
                            <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100">
                                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-6">About the Provider</h3>
                                <div className="flex items-center gap-4 mb-6">
                                    {provider.avatarUrl ? (
                                        <img src={provider.avatarUrl} alt={provider.name} className="w-16 h-16 rounded-2xl object-cover ring-4 ring-slate-50 shadow-sm" />
                                    ) : (
                                        <div className="w-16 h-16 rounded-2xl bg-indigo-600 flex items-center justify-center text-white text-2xl font-black shadow-lg">
                                            {provider.name?.charAt(0)}
                                        </div>
                                    )}
                                    <div>
                                        <h4 className="font-extrabold text-slate-900 text-lg group-hover:text-indigo-600 transition-colors">
                                            {provider.name}
                                        </h4>
                                        <p className="text-xs font-bold text-slate-400 flex items-center gap-1">
                                            <MapPin className="w-3 h-3" /> {provider.location || 'Remote'}
                                        </p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3 mb-6">
                                    <div className="bg-slate-50 p-3 rounded-2xl border border-slate-100">
                                        <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 text-center">Jobs</span>
                                        <span className="block text-sm font-black text-slate-900 text-center">{provider.jobsCompleted || 0}</span>
                                    </div>
                                    <div className="bg-slate-50 p-3 rounded-2xl border border-slate-100">
                                        <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 text-center">Rating</span>
                                        <div className="flex items-center justify-center gap-1">
                                            <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                                            <span className="text-sm font-black text-slate-900">{provider.rating || 0}</span>
                                        </div>
                                    </div>
                                </div>

                                <p className="text-sm text-slate-500 line-clamp-3 mb-6 italic leading-relaxed">
                                    "{provider.bio || 'Professional service provider committed to quality delivery.'}"
                                </p>

                                <Link
                                    to={`/profile/va/${provider.id}`}
                                    className="w-full py-3 rounded-xl border-2 border-indigo-50 text-indigo-600 text-sm font-bold hover:bg-indigo-50 hover:border-indigo-100 transition-all flex items-center justify-center gap-2"
                                >
                                    View Profile <ArrowLeft className="w-4 h-4 rotate-180" />
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {service && provider && (
                <BookingModal
                    service={service}
                    provider={provider}
                    isOpen={isBookingModalOpen}
                    onClose={() => setIsBookingModalOpen(false)}
                    onSubmit={handleBookingSubmit}
                    currentUser={user}
                    currentProfile={userProfile}
                />
            )}
        </div>
    );
};

export default ServicePage;
